import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { Button, Icon, Message, Popup } from "semantic-ui-react";
import { styled } from "styled-components";

import { initUseQuery } from "simplydo/core/query";
import { OpenAPI } from "simplydo/interfaces";

import api from "api";
import { useAppSelector } from "store";

import IdeaAssessmentReport from "./IdeaAssessmentReport";

const CompactButton = styled(Button)<{ compact: boolean }>`
  padding: ${({ compact }) =>
    compact ? "6px 9px 4px 8px" : ".85em 1.1em .78571429em 1em"} !important;

  display: inline-flex !important;
  flex-direction: row;
  flex-wrap: nowrap;

  i {
    margin: 0 !important;
  }
  i:first-child {
    font-size: ${({ compact }) => (compact ? "10px" : "11px")} !important;
    margin-right: 2px !important;
  }

  .label {
    font-size: ${({ compact }) => (compact ? "12px" : undefined)} !important;
    padding: ${({ compact }) => (compact ? "4px 8px" : undefined)} !important;
  }
`;

type IdeaAssessmentProgressProps = {
  idea: OpenAPI.Assign<
    OpenAPI.Schemas["Idea"],
    OpenAPI.Schemas["AssignIdeaChallenges"],
    OpenAPI.Schemas["AssignIdeaAssessmentDetails"],
    OpenAPI.Schemas["AssignIdeaTemplates"]
  >;
  challenge?: OpenAPI.Assign<
    OpenAPI.Schemas["Challenge"],
    OpenAPI.Schemas["AssignChallengeIdeaTemplate"]
  >;
  compact?: boolean;
};

export const IdeaAssessmentProgress = ({
  idea,
  challenge,
  compact = false,
}: IdeaAssessmentProgressProps) => {
  const invitationQuery = useQuery(
    initUseQuery(api.invitations.getForType, ["ideaAssessor", idea.challenge]),
  );
  const { t } = useTranslation();

  const hasInvitedExternalAssessors =
    idea.invitedExternalAssessors && idea.invitedExternalAssessors?.length > 0;
  const hasAssessments =
    (idea.assessments && idea.assessments?.length > 0) ||
    hasInvitedExternalAssessors;
  const anyAssessmentHasConflict = idea.assessments?.some(
    (assessment) => assessment.conflictOfInterest?.hasConflictOfInterest,
  );
  const allAssessmentsAreSubmitted = idea.assessments?.every(
    (assessment) => assessment.isSubmitted,
  );

  const user = useAppSelector((state) => state.user);
  const userAssessment = idea.assessments?.find(
    (assessment) => assessment.user === user._id,
  );

  const allAssessmentsComplete =
    allAssessmentsAreSubmitted && !hasInvitedExternalAssessors;

  const pendingInvitations = invitationQuery.data?.invitations.filter((i) =>
    i?.forIdeas?.includes(idea._id),
  );

  return (
    <Popup
      mountNode={document.getElementById("semantic-modal-mount-node")}
      style={{ zIndex: 990 }}
      disabled={!hasAssessments}
      trigger={
        hasAssessments ?
          <CompactButton
            size={compact ? "mini" : "tiny"}
            compact={compact}
            icon="percent"
            content={
              <Icon
                name={
                  anyAssessmentHasConflict ? "exclamation triangle"
                  : !allAssessmentsComplete ?
                    "wait"
                  : "check"
                }
              />
            }
            color={
              anyAssessmentHasConflict ? "red"
              : !allAssessmentsComplete ?
                "yellow"
              : "green"
            }
            label={{
              basic: true,
              size: compact ? "mini" : "tiny",
              pointing: "left",
              content: (
                <span style={{ whiteSpace: "nowrap" }}>
                  {idea?.assessmentDetails?.submittedCount || 0}
                  {" / "}
                  {(idea?.assessmentDetails?.totalCount || 0) +
                    (pendingInvitations?.length || 0)}
                </span>
              ),
            }}
          />
        : null
      }
      on="click"
      basic
      content={
        <div style={{ width: 500 }}>
          {userAssessment && !userAssessment?.isSubmitted ?
            <Message>
              You are assigned as an assessor for this {t("generic.idea")} and
              still need to assess it.
            </Message>
          : null}
          <IdeaAssessmentReport idea={idea} challenge={challenge} />
        </div>
      }
    />
  );
};
